import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Drawer,
  Grid,
  makeStyles,
  MenuItem,
  Typography,
} from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import { errorMessages } from 'common/errorMessages.strings';
import { tipoRepresentanteLegalDescriptions } from 'common/genericDescriptions';
import { tipoRepresentanteLegalOptions, booleanOptions, BooleanValue } from 'common/genericOptions';
import { TipoRepresentanteLegal } from 'common/genericTypes';
import { CpfMask, TelefoneMask } from 'components/masks';
import moment from 'moment';
import { Button, Dropdown, MoreOptionsMenu, SmSpace, TextField, DatePicker } from 'm2m.components';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { RepresentanteLegalState, updateRepresentantes } from 'store/modules/participantes/registro';
import { addAlert } from 'store/modules/snackBar';
import { validaCpf, validaEmail } from 'utils/validation';
import { addTelefoneMask } from 'utils/mask';
import { v4 as uuid } from 'uuid';

interface Props {
  isStepFulfilled(isFulfilled: boolean): void;
}

const useStyles = makeStyles(() => ({
  fixedHeight: {
    height: '220px',
  },
  fixedHeightCentered: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '276px',
  },
  wrapTxt: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  overflow: {
    overflow: 'hidden',
  },
}));

const RepresentantesLegais: React.FC<Props> = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { isStepFulfilled } = props;
  const [openNewRepresentanteDrawer, setOpenNewRepresentanteDrawer] = useState<boolean>(false);
  const [representanteLegalEdit, setRepresentanteLegalEdit] = useState<RepresentanteLegalState>();
  const [openDeleteConfirmDrawer, setOpenDeleteConfirmDrawer] = useState<boolean>(false);
  const [nome, setNome] = useState<string>('');
  const [cpf, setCpf] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [tipo, setTipo] = useState<TipoRepresentanteLegal>();
  const [cargo, setCargo] = useState<string>('');
  const [telefone, setTelefone] = useState<string>('');
  const [outorgaTotal, setOutorgaTotal] = useState<boolean | null>(null);
  const [dataExpiracao, setDataExpiracao] = useState<moment.Moment | null>(null);

  const participante = useSelector((state: RootState) => state.participante);

  useEffect(() => {
    isStepFulfilled(participante.representantesLegais.length > 0);
  }, [participante.representantesLegais, props]);

  const handleSubmitClick = () => {
    if (!validaCpf(cpf)) {
      dispatch(addAlert({ severity: 'error', message: errorMessages.participante.cpfInvalido }));
      return;
    }
    if (!validaEmail(email)) {
      dispatch(addAlert({ severity: 'error', message: errorMessages.participante.emailInvalido }));
      return;
    }
    if (!cargo?.trim()) {
      dispatch(addAlert({ severity: 'error', message: 'Preencha o campo Cargo.' }));
      return;
    }
    if (!telefone?.trim()) {
      dispatch(addAlert({ severity: 'error', message: 'Preencha o campo Telefone.' }));
      return;
    }
    if (outorgaTotal === null) {
      dispatch(addAlert({ severity: 'error', message: 'Selecione a opção de Outorga Total.' }));
      return;
    }
    if (!dataExpiracao) {
      dispatch(addAlert({ severity: 'error', message: 'Informe a Data de Expiração.' }));
      return;
    }
    if (participante.representantesLegais.some((representante) => representante.email === email)) {
      dispatch(addAlert({ severity: 'error', message: errorMessages.participante.emailDuplicado }));
      return;
    }
    if (participante.usuarios.some((usuario) => usuario.email === email)){
      dispatch(addAlert({ severity: 'error', message: errorMessages.participante.emailUsuario }));
      return;
    }
    if (participante.usuarios.some((usuario) => usuario.cpf === cpf)){
      dispatch(addAlert({ severity: 'error', message: errorMessages.participante.cpfUsuario }));
      return;
    }
    if (participante.representantesLegais.some((representante) => representante.cpf === cpf)) {
      dispatch(addAlert({ severity: 'error', message: errorMessages.participante.cpfDuplicado }));
      return;
    }

    const updatedRepresentantes = participante.representantesLegais.concat({
      nome,
      cpf,
      tipo,
      email,
      id: uuid(),
      cargo: cargo,
      telefone: telefone,
      outorgaTotal: outorgaTotal,
      dataExpiracao: dataExpiracao ? dataExpiracao.format('YYYY-MM-DD') : undefined,
    } as RepresentanteLegalState);
    dispatch(updateRepresentantes(updatedRepresentantes));
    setOpenNewRepresentanteDrawer(false);
    setNome('');
    setCpf('');
    setEmail('');
    setTipo(undefined);
    setCargo('');
    setTelefone('');
    setOutorgaTotal(null);
    setDataExpiracao(null);
  };

  const handleDeleteClick = (id: string) => {
    setRepresentanteLegalEdit(participante.representantesLegais.find((representante) => representante.id === id));
    setOpenDeleteConfirmDrawer(true);
  };

  const handleDeleteItem = () => {
    const updatedRepresentates = participante.representantesLegais.filter(
      (representante) => representante.id !== representanteLegalEdit?.id
    );
    dispatch(updateRepresentantes(updatedRepresentates));
    setOpenDeleteConfirmDrawer(false);
  };

  return (
    <>
      <Grid container spacing={3} style={{ paddingRight: '12px', width: '100%' }}>
        {participante.representantesLegais.map((representanteLegal) => (
          <Grid item sm={4} key={representanteLegal.cpf}>
            <Card variant="outlined">
              <CardHeader
                style={{ paddingBottom: 0 }}
                classes={{ content: classes.overflow }}
                action={
                  <MoreOptionsMenu>
                    <MenuItem onClick={() => handleDeleteClick(representanteLegal.id)}>
                      <DeleteIcon fontSize="small" style={{ marginRight: '10px' }} />
                      {'Excluir'}
                    </MenuItem>
                  </MoreOptionsMenu>
                }
                title={
                  <Typography variant="h3" className={classes.wrapTxt}>
                    {representanteLegal.nome}
                  </Typography>
                }
              />
              <CardContent className={classes.fixedHeight}>
                <Grid container>
                  <Grid item md={6}>
                    <Typography variant="body2">{'Perfil:'}</Typography>
                    <Typography variant="body1">
                      {tipoRepresentanteLegalDescriptions[representanteLegal.tipo]}
                    </Typography>
                  </Grid>
                  <Grid item md={6}>
                    <Typography variant="body2">{'Email:'}</Typography>
                    <Typography variant="body1">{representanteLegal.email}</Typography>
                  </Grid>
                  <Grid item md={6}>
                    <Typography variant="body2">{'CPF:'}</Typography>
                    <Typography variant="body1">{representanteLegal.cpf}</Typography>
                  </Grid>
                  <Grid item md={6}>
                    <Typography variant="body2">{'Cargo:'}</Typography>
                    <Typography variant="body1">{representanteLegal.cargo || '-'}</Typography>
                  </Grid>
                  <Grid item md={6}>
                    <Typography variant="body2">{'Telefone:'}</Typography>
                    <Typography variant="body1">{representanteLegal.telefone ? addTelefoneMask(representanteLegal.telefone) : '-'}</Typography>
                  </Grid>
                  <Grid item md={6}>
                    <Typography variant="body2">{'Outorga Total:'}</Typography>
                    <Typography variant="body1">{representanteLegal.outorgaTotal ? 'Sim' : 'Não'}</Typography>
                  </Grid>
                  <Grid item md={12}>
                    <Typography variant="body2">{'Data de Expiração:'}</Typography>
                    <Typography variant="body1">
                      {representanteLegal.dataExpiracao
                        ? moment(representanteLegal.dataExpiracao as any).format('DD/MM/YYYY')
                        : '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
        <Grid item sm={4}>
          <Card variant="outlined">
            <CardContent className={classes.fixedHeightCentered}>
              <Button onClick={() => setOpenNewRepresentanteDrawer(true)}>
                <div>
                  <AddCircleIcon fontSize="small" />
                  <SmSpace />
                  <Typography variant="subtitle1">{'Novo Representante'}</Typography>
                </div>
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Drawer
        anchor="right"
        open={openNewRepresentanteDrawer}
        onClose={() => setOpenNewRepresentanteDrawer(false)}
        PaperProps={{ style: { padding: '40px', width: 465 } }}
      >
        <div>
          <Typography variant="h3">{'Adicionar Representantes Legais'}</Typography>
          <SmSpace />
          <Divider variant="fullWidth" />
          <SmSpace />
          <TextField
            label="Nome"
            value={nome}
            required
            onChange={(value) => setNome(value.replace(/[^À-ÿa-z ]/gi, ''))}
          />
          <SmSpace />
          <TextField label="CPF" value={CpfMask(cpf)} required onChange={setCpf} />
          <SmSpace />
          <TextField label="Email" value={email} required onChange={setEmail} />
          <SmSpace />
          <TextField label="Cargo" value={cargo} required onChange={setCargo} />
          <SmSpace />
          <TextField label="Telefone" value={TelefoneMask(telefone)} required onChange={setTelefone} />
          <SmSpace />
          <Dropdown
            label="Outorga Total"
            options={booleanOptions}
            value={outorgaTotal === null ? '' : outorgaTotal ? BooleanValue.Yes : BooleanValue.No}
            required
            onChange={(value) =>
              setOutorgaTotal(value === BooleanValue.Yes ? true : value === BooleanValue.No ? false : null)
            }
          />
          <SmSpace />
          <DatePicker label="Data de Expiração" value={dataExpiracao} required onChange={setDataExpiracao} />
          <SmSpace />
          <Dropdown
            label="Tipo"
            options={tipoRepresentanteLegalOptions}
            value={tipo ?? ''}
            required
            onChange={(value) => setTipo(value as TipoRepresentanteLegal)}
          />
          <SmSpace />
          <Button
            variant="contained"
            disabled={!(
              tipo &&
              nome &&
              cpf &&
              email &&
              cargo &&
              telefone &&
              outorgaTotal !== null &&
              dataExpiracao
            )}
            fullWidth
            color={'primary'}
            onClick={handleSubmitClick}
          >
            {'Adicionar Representante'}
          </Button>
          <SmSpace />
        </div>
      </Drawer>
      <Drawer
        anchor="right"
        open={openDeleteConfirmDrawer}
        onClose={() => setOpenDeleteConfirmDrawer(false)}
        PaperProps={{ style: { padding: '40px', width: 465 } }}
      >
        <div>
          <Typography variant="h3">{'Excluir representante'}</Typography>
          <SmSpace />
          <Typography variant="body2">{`Tem certeza que deseja excluir o representante ${representanteLegalEdit?.nome}?`}</Typography>
          <SmSpace />
          <Divider variant="fullWidth" />
          <SmSpace />
          <Button onClick={handleDeleteItem} variant="contained" color="error" fullWidth>
            {'Sim, excluir'}
          </Button>
          <SmSpace />
          <Button onClick={() => setOpenDeleteConfirmDrawer(false)} variant="outlined" fullWidth>
            {'Cancelar'}
          </Button>
        </div>
      </Drawer>
    </>
  );
};

export default RepresentantesLegais;
